import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Stack,
  List,
  Typography,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Button,
  Divider,
  Box,
} from "@mui/material";
import { IconButton } from "@mui/material";
import { ReactComponent as CloseOutlined } from "../../assets/icons/close_icon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit_icon.svg";
import { ReactComponent as SubscriptionIcon } from "../../assets/icons/subscription_icon.svg";
import { ReactComponent as HelpIcon } from "../../assets/icons/help_icon.svg";
import { ReactComponent as PrivacyIcon } from "../../assets/icons/privacy_icon.svg";
import { ReactComponent as TermsIcon } from "../../assets/icons/terms_icon.svg";
import { ReactComponent as LogoutIcon } from "../../assets/icons/logout_icon.svg";
import { ReactComponent as RightArrowIcon } from "../../assets/icons/right_arrow.svg";
import { useState } from "react";
import HelpAndSupportDialog from "./HelpAndSupportDialog";
import SubscriptionDialog from "./SubscriptionDialog";
import CreateAccountDialog from "./CreateAccountDialog";
import ConfirmationDialog from "./ConfirmationDialog";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const SettingsDialog = ({ open, handleDialogClose }) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const navigate = useNavigate();

  // Handle Help and support dialog
  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  // Handle name edit
  const [openEditName, setOpenEditName] = useState(false);
  const handleOpenEditName = () => setOpenEditName(true);
  const handleCloseEditName = () => setOpenEditName(false);

  // Handle subscription dialog
  const [openSubscription, setOpenSubscription] = useState(false);
  const handleOpenSubscription = () => setOpenSubscription(true);
  const handleCloseSubscription = () => setOpenSubscription(false);

  // Handle confirmation dialog
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  // Handle title and description for confirmation dialog for logout and
  // delete actions
  const [confirmationTitle, setConfirmationTitle] = useState("");
  const [confirmationDescription, setConfirmationDescription] = useState("");

  // Check if user has subscription before deleting account
  const currExpiry = useSelector((state) => state.user.planExpiry);
  const authorized = Date.now() < currExpiry;

  // Check if subscription canceled
  const planCancel = useSelector((state) => state.user.planCancel);

  // Open terms and conditions
  const handleOpenTerms = () => navigate("/terms-and-conditions");

  // Check if subscription canceled
  const handleOpenPrivacy = () => navigate("/privacy-policy");

  const listData = React.useRef([
    [<EditIcon />, "Edit full name", handleOpenEditName],
    [<SubscriptionIcon />, "Subscription", handleOpenSubscription],
    [<HelpIcon />, "Help & Support", handleOpenHelp],
    [<PrivacyIcon />, "Privacy & security", handleOpenPrivacy],
    [<TermsIcon />, "Terms & conditions", handleOpenTerms],
  ]);

  return (
    <>
      <Dialog
        scroll="body"
        open={open}
        onClose={handleDialogClose}
        disableScrollLock={false}
        fullWidth
        sx={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        slotProps={{
          paper: {
            component: "form",
            anchor: "top",
            sx: {
              p: {
                xs: "10px 10px 20px 10px !important",
                sm: "30px !important",
              },
              maxHeight: "100vh",
              borderRadius: { xs: "20px !important", sm: "50px !important" },
            },
          },
        }}
      >
        <DialogTitle
          variant="h3"
          sx={{
            textAlign: "center",
            p: 0,
            mt: { sm: "10px", xs: "20px" },
            fontWeight: 700,
          }}
        >
          ACCOUNT SETTINGS!
        </DialogTitle>

        <Typography
          sx={{
            color: "white",
            textAlign: "center",
            fontFamily: "secondaryFontFamily",
            fontSize: "14px",
            fontWeight: 300,
            mb: "20px",
          }}
        >
          Update your profile, security and preferences in one place
        </Typography>

        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: { sm: 20, xs: 10 },
            top: { sm: 20, xs: 10 },
            fontSize: "25px",
            color: "#999999",
          }}
        >
          {" "}
          <CloseOutlined />
        </IconButton>
        <Stack direction={"column"}>
          <List disablePadding>
            {listData.current.map((item, index) => (
              <Box key={index}>
                <ListItem
                  key={index}
                  sx={{
                    fontSize: "20px !important",
                    p: { sm: "0 20px", xs: "0 10px" },
                  }}
                >
                  <ListItemButton
                    onClick={() => (item[2] !== null ? item[2]() : null)}
                    sx={{
                      paddingTop: { sm: "20px", xs: "15px" },
                      paddingBottom: "10px !important",
                    }}
                  >
                    <ListItemIcon>{item[0]}</ListItemIcon>
                    <ListItemText
                      primary={item[1]}
                      sx={{
                        ".MuiListItemText-primary": {
                          fontSize: {
                            sm: "20px !important",
                            xs: "16px !important",
                          },
                          fontWeight: 400,
                        },
                      }}
                    />
                    <ListItemIcon edge="end">
                      <RightArrowIcon style={{ marginLeft: "auto" }} />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
                <Divider variant="middle" />
              </Box>
            ))}
            <ListItem
              sx={{
                fontSize: "20px !important",
                p: { sm: "0 20px", xs: "0 10px" },
              }}
            >
              <ListItemButton
                sx={{
                  paddingBottom: "10px !important",
                  paddingTop: "20px !important",
                }}
                onClick={() => {
                  setConfirmationTitle("LOGOUT");
                  setConfirmationDescription("Do you really want to logout?");
                  handleOpenConfirm();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary={"Logout"}
                  sx={{
                    ".MuiListItemText-primary": {
                      fontSize: {
                        sm: "20px !important",
                        xs: "16px !important",
                      },
                      fontWeight: 400,
                    },
                  }}
                />
                <ListItemIcon edge="end">
                  <RightArrowIcon style={{ marginLeft: "auto" }} />
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
          <Divider variant="middle" />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ml: "35px",
              mr: "15px",
              mt: "10px",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="text"
              onClick={() => {
                if (authorized && planCancel === null) {
                  setConfirmationTitle("Cancel Subscription");
                  setConfirmationDescription(
                    "Please cancel your subscription before deleting your account."
                  );
                } else {
                  setConfirmationTitle("DELETE ACCOUNT");
                  setConfirmationDescription(
                    "Deleting your account removes all of your information from our database. This cannot be undone."
                  );
                }
                handleOpenConfirm();
              }}
              sx={{
                p: 0,
                fontSize: { sm: "20px !important", xs: "16px !important" },
                fontWeight: 400,
                fontFamily: "secondaryFontFamily",
                textTransform: "unset",
                width: "fit-content",
              }}
            >
              Delete account?
            </Button>
            {authorized && (
              <Typography
                sx={{
                  color: "white",
                  fontFamily: "secondaryFontFamily",
                  fontSize: "14px",
                }}
              >
                {`Your Subscription ${
                  planCancel ? "ends" : "renews"
                } on: ${dayjs(currExpiry).format("DD-MM-YYYY")}`}
              </Typography>
            )}
          </Box>
        </Stack>
      </Dialog>

      <ConfirmationDialog
        title={confirmationTitle}
        description={confirmationDescription}
        open={openConfirm}
        handleDialogClose={handleCloseConfirm}
      />

      <HelpAndSupportDialog
        open={openHelp}
        handleDialogClose={handleCloseHelp}
      />

      <CreateAccountDialog
        editName={true}
        open={openEditName}
        handleDialogClose={handleCloseEditName}
      />

      <SubscriptionDialog
        open={openSubscription}
        handleDialogClose={handleCloseSubscription}
      />
    </>
  );
};

export default SettingsDialog;
